import { Button } from '@zealot/ui/components/button';
import { EmptyState } from '@zealot/ui/components/empty-state';
import { Skeleton } from '@zealot/ui/components/skeleton';
import { cn } from '@zealot/ui/lib/utils';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  OMNI_EMBED_FILTER_CHANGE_COMMAND,
  type OmniDashboardFiltersPayload,
  type OmniEmbedCommand,
  type OmniEmbedEvent,
  type OmniErrorPayload,
  type OmniPageChangedPayload,
  type OmniStatusPayload,
  omniEmbedOrigin,
} from './omniEmbedProtocol';
import { useOmniEmbedEvents } from './useOmniEmbedEvents';

type OmniEmbedStatus = 'loading' | 'ready' | 'error';

type OmniEmbedState = OmniEmbedStatus | 'invalid' | 'expired' | 'connecting' | 'loaded';

// gloss
type OmniEmbedFrameControls = {
  changeDashboardFilterByUrlParameter: (filterUrlParameter: string) => void;
};

type OmniEmbedProps = {
  status: OmniEmbedStatus;
  url?: string | null;
  expiresAt?: string | Date | null;
  title: string;
  errorMessage?: string;
  onRetry?: () => void;
  onExpired?: () => void;
  onError?: (payload: OmniErrorPayload) => void;
  onPageChanged?: (payload: OmniPageChangedPayload) => void;
  onStatus?: (payload: OmniStatusPayload) => void;
  onDashboardFilters?: (payload: OmniDashboardFiltersPayload, controls: OmniEmbedFrameControls) => void;
  allow?: string;
  shouldFollowContentHeight?: boolean;
  className?: string;
};

const DEFAULT_ALLOW = 'clipboard-write; fullscreen';

const isPast = (expiresAt: string | Date | null | undefined): boolean => {
  if (!expiresAt) return false;
  const at = expiresAt instanceof Date ? expiresAt.getTime() : Date.parse(expiresAt);
  return !Number.isNaN(at) && at <= Date.now();
};

const OmniEmbedShell = ({
  state,
  isBusy = false,
  className,
  children,
}: {
  state: OmniEmbedState;
  isBusy?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <div
    data-slot="omni-embed"
    data-state={state}
    aria-busy={isBusy}
    className={cn(
      'relative flex min-h-[480px] w-full flex-col overflow-hidden rounded-lg border border-border bg-card',
      className,
    )}
  >
    {children}
  </div>
);

const OmniEmbedSkeleton = () => (
  <div data-slot="omni-embed-skeleton" className="absolute inset-0 flex flex-col gap-4 bg-card p-6">
    <Skeleton className="h-8 w-1/3" />
    <div className="grid grid-cols-3 gap-4">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
    <Skeleton className="min-h-40 flex-1" />
  </div>
);

const RetryAction = ({ onRetry }: { onRetry?: () => void }) =>
  onRetry ? (
    <Button variant="outline" size="sm" onClick={onRetry}>
      <RefreshCw data-icon="inline-start" />
      Retry
    </Button>
  ) : null;

const OmniEmbedNotice = ({
  state,
  icon,
  title,
  description,
  onRetry,
  className,
}: {
  state: OmniEmbedState;
  icon: ReactNode;
  title: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}) => (
  <OmniEmbedShell state={state} className={className}>
    <EmptyState
      variant="inline"
      className="flex-1"
      icon={icon}
      title={title}
      description={description}
      action={<RetryAction onRetry={onRetry} />}
    />
  </OmniEmbedShell>
);

type OmniEmbedFrameProps = Omit<OmniEmbedProps, 'status' | 'url' | 'expiresAt' | 'errorMessage'> & {
  url: string;
  origin: string;
};

// gloss
const OmniEmbedFrame = ({
  url,
  origin,
  title,
  onError,
  onPageChanged,
  onStatus,
  onDashboardFilters,
  allow = DEFAULT_ALLOW,
  shouldFollowContentHeight = true,
  className,
}: OmniEmbedFrameProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const controls = useMemo<OmniEmbedFrameControls>(
    () => ({
      changeDashboardFilterByUrlParameter: (filterUrlParameter) => {
        const command: OmniEmbedCommand = {
          name: OMNI_EMBED_FILTER_CHANGE_COMMAND,
          payload: { filterUrlParameter },
        };
        iframeRef.current?.contentWindow?.postMessage(command, origin);
      },
    }),
    [origin],
  );

  useOmniEmbedEvents({
    iframeRef,
    origin,
    onEvent: (event: OmniEmbedEvent) => {
      switch (event.name) {
        case 'size':
          if (shouldFollowContentHeight) setHeight(event.payload.height);
          break;
        case 'page:changed':
          onPageChanged?.(event.payload);
          break;
        case 'error':
          onError?.(event.payload);
          break;
        case 'status':
          onStatus?.(event.payload);
          break;
        case 'dashboard:filters':
          onDashboardFilters?.(event.payload, controls);
          break;
        default:
          break;
      }
    },
  });

  return (
    <OmniEmbedShell state={hasLoaded ? 'loaded' : 'connecting'} isBusy={!hasLoaded} className={className}>
      <iframe
        ref={iframeRef}
        data-slot="omni-embed-frame"
        title={title}
        src={url}
        allow={allow}
        onLoad={() => setHasLoaded(true)}
        className="block h-full min-h-[inherit] w-full border-0"
        style={height ? { height } : undefined}
      />
      {!hasLoaded && <OmniEmbedSkeleton />}
    </OmniEmbedShell>
  );
};

// gloss
const OmniEmbedGate = ({
  url,
  expiresAt,
  onExpired,
  ...frameProps
}: OmniEmbedFrameProps & Pick<OmniEmbedProps, 'expiresAt' | 'onExpired'>) => {
  const [isStale] = useState(() => isPast(expiresAt));
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (isStale) onExpiredRef.current?.();
  }, [isStale]);

  if (isStale) {
    return (
      <OmniEmbedNotice
        state="expired"
        icon={<Clock />}
        title="This report link expired before it opened"
        description="Report links are only valid for a few minutes. Request a fresh one to continue."
        onRetry={frameProps.onRetry}
        className={frameProps.className}
      />
    );
  }

  return <OmniEmbedFrame url={url} {...frameProps} />;
};

// gloss
const OmniEmbed = ({ status, url, errorMessage, ...rest }: OmniEmbedProps) => {
  const { className, onRetry } = rest;

  if (status === 'loading') {
    return (
      <OmniEmbedShell state="loading" isBusy className={className}>
        <OmniEmbedSkeleton />
      </OmniEmbedShell>
    );
  }

  if (status === 'error') {
    return (
      <OmniEmbedNotice
        state="error"
        icon={<AlertTriangle />}
        title="Reporting could not be loaded"
        description={errorMessage}
        onRetry={onRetry}
        className={className}
      />
    );
  }

  const origin = omniEmbedOrigin(url);
  if (!url || !origin) {
    return (
      <OmniEmbedNotice
        state="invalid"
        icon={<AlertTriangle />}
        title="Reporting could not be loaded"
        description="The report link was not a secure Omni address."
        onRetry={onRetry}
        className={className}
      />
    );
  }

  return <OmniEmbedGate key={url} url={url} origin={origin} {...rest} />;
};

export type { OmniEmbedFrameControls, OmniEmbedProps, OmniEmbedState, OmniEmbedStatus };
export { OmniEmbed };
